<template>
  <div v-if="dataReady" class="p-8">
    <!-- Confirm hide/show dialog -->


    <!-- Month & Year Selection -->
    <div class="flex space-x-4 items-center mb-4">
      <label for="month" class="text-gray-900">Month:</label>
      <select id="month" v-model="selectedMonth" class="border p-2 rounded">
        <option v-for="(monthName, index) in months" :key="index" :value="index">
          {{ monthName }}
        </option>
      </select>
      <label for="year" class="text-gray-900">Year:</label>
      <select id="year" v-model="selectedYear" class="border p-2 rounded">
        <option v-for="year in years" :key="year" :value="year">
          {{ year }}
        </option>
      </select>
      <!-- Today Button -->
      <button @click="goToToday" class="bg-[#B203BC] text-white px-4 py-2 rounded transition hover:bg-[#56039A]">
        Today
      </button>
      <!-- Toggle for Screenshot Mode -->
      <button @click="showFullText = !showFullText" class="bg-gray-300 text-black px-4 py-2 rounded ml-4">
        {{ showFullText ? 'Collapsed Mode' : 'Expand Mode' }}
      </button>
      <!-- New: Capture Screenshot Button -->
      <button @click="captureScreenshot" class="bg-gray-300 text-black px-4 py-2 rounded ml-4">
        Capture Screenshot
      </button>
          <!-- Breadcrumbs for Screenshot Captures -->
          <div v-if="screenshotBreadcrumbs.length" class="mt-2 text-sm text-gray-700">
  <transition-group name="fade" tag="ul">
    <li v-for="(log, idx) in screenshotBreadcrumbs" :key="idx">
      {{ log }}
    </li>
  </transition-group>
</div>
    </div>


    <!-- Competitor Filter Buttons -->
    <div class="mb-4">
      <div class="flex flex-wrap gap-4">
        <button
  v-for="filter in competitorFilters"
  :key="filter.name"
  :data-filter="filter.name"
  :class="[
    'filter-btn',
    { active: activeCompetitorFilters.includes(filter.name) },
    'bg-gray-200 px-4 py-2 rounded-full flex items-center space-x-2 border'
  ]"
  @click="toggleCompetitorFilter(filter.name)"
>
          <span :class="[
            'w-6 h-6 rounded-full flex items-center justify-center text-xs',
            filter.badgeClass,
            filter.textClass
          ]">
            <span>{{ filter.label }}</span>
          </span>
          <span>{{ filter.displayName }}</span>
        </button>
      </div>
    </div>

    <!-- Event Type Filter Buttons -->
    <div class="mb-4">
      <div class="inline-flex space-x-4">
        <button
          v-for="filter in eventTypeFilters"
          :key="filter.name"
          :data-filter="filter.name"
          :class="[
            'filter-btn',
            {
              active: filter.name === 'all'
                ? activeEventTypeFilters.length === 0
                : activeEventTypeFilters.includes(filter.name)
            },
            'bg-gray-200 px-4 py-2 rounded-full flex items-center space-x-2 border'
          ]"
          @click="toggleEventTypeFilter(filter.name)"
        >
          <span :class="[
            'w-6 h-6 rounded-full flex items-center justify-center text-xs',
            filter.badgeClass,
            filter.textClass
          ]">
            <span>{{ filter.label }}</span>
          </span>
          <span>{{ filter.displayName }}</span>
        </button>
      </div>
    </div>

    <div ref="screenshotArea">
      <h1 class="text-2xl font-bold mb-4">
        Competitor Campaigns:
        <span class="inline-flex items-center">
          <button @click="prevMonth" class="p-1 text-gray-700 hover:text-gray-900 focus:outline-none">
            <i class="fas fa-chevron-left"></i>
          </button>
          <span class="mx-2">{{ months[selectedMonth] }} {{ selectedYear }}</span>
          <button @click="nextMonth" class="p-1 text-gray-700 hover:text-gray-900 focus:outline-none">
            <i class="fas fa-chevron-right"></i>
          </button>
        </span>
      </h1>

      <!-- Day Row -->
      <div class="flex mb-2">
        <div class="w-20"></div>
        <div class="relative flex-1">
          <div class="grid gap-0" :style="{ gridTemplateColumns: 'repeat(' + daysInMonth + ', 1fr)' }">
            <div
              v-for="day in daysInMonthArray"
              :key="'day-header-' + day"
              class="text-center border-b border-r last:border-r-0"
              :class="{
                'bg-gray-100': weekendColumns[day - 1],
                'bg-[#B203BC] text-white font-bold border rounded': todayDay === day
              }"
            >
              {{ day }}
            </div>
          </div>
        </div>
      </div>

      <!-- Planner Row: Only display "my own" campaigns (no competitorId) -->
      <div v-if="showPlanned" class="relative">
        <div class="flex">
          <div class="w-20 flex items-center justify-center">
            <div class="w-12 h-10 border rounded flex items-center justify-center bg-gray-200 text-gray-900">
              Plan
            </div>
          </div>
          <div class="relative flex-1 border-b-2 border-gray-200">
            <!-- Background grid for planner row -->
            <div class="absolute inset-0 grid gap-0" :style="{ gridTemplateColumns: 'repeat(' + daysInMonth + ', 1fr)' }">
              <template v-for="day in daysInMonthArray" :key="'planner-bg-' + day">
                <div :class="{ 'bg-gray-100': weekendColumns[day - 1] }"></div>
              </template>
            </div>
            <!-- My own campaigns grid -->
            <div class="grid gap-0 relative" :style="{ gridTemplateColumns: 'repeat(' + daysInMonth + ', 1fr)' }">
              <div
                v-for="event in plannedEvents"
                :key="'planned-' + event.id"
                class="fluted-event-item relative rounded-md cursor-default"
                :class="[
                  event.type === 'Sale'
                    ? 'bg-red-100 text-black'
                    : event.type === 'Campaign'
                      ? 'bg-blue-100 text-black'
                      : 'bg-violet-100 text-black'
                ]"
                :style="{
                  gridColumn: event.startDay + ' / ' + (event.startDay + event.duration),
                  padding: '2px 4px',
                  margin: '2px'
                }"
                @mouseenter="hoveredEventId = event.id"
                @mouseleave="hoveredEventId = null"
              >
                <template v-if="event.duration > 1">
                  <span v-if="showFullText" v-auto-resize="true" class="full-text">
                    {{ event.incent }}
                  </span>
                  <span v-else class="truncate" v-check-truncate="event.id">
                    {{ event.incent }}
                  </span>
                </template>
                <template v-else>
                  <span v-if="showFullText" v-auto-resize="true" class="full-text">
                    {{ event.incent }}
                  </span>
                  <span v-else class="flex items-center justify-center h-full w-full">
                    ...
                  </span>
                </template>
                <div
                  v-if="hoveredEventId === event.id && (event.duration === 1 || truncatedMap[event.id])"
                  class="absolute bg-black text-white text-xs rounded-md px-2 py-1 shadow-lg transition-opacity duration-300"
                  :style="{
                    bottom: 'calc(100% + 4px)',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    whiteSpace: 'pre-wrap',
                    maxWidth: '200px',
                    zIndex: 50,
                    pointerEvents: 'none'
                  }"
                >
                  {{ event.incent }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Competitor Overview Rows -->
      <div>
        <template v-if="displayCompetitors.length > 0">
          <div
            v-for="(competitor, index) in displayCompetitors"
            :key="competitor.id"
            class="flex relative"
          >
            <!-- Competitor bubble with name below -->
            <div class="w-20 flex flex-col items-center justify-center cursor-pointer group" @click="openAddCampaignModal(competitor)">
              <div
                :class="[
                  'relative w-10 h-10 border rounded-full flex items-center justify-center',
                  'overflow-hidden transition-colors duration-300 group-hover:bg-[#B203BC]',
                  competitor.logoBgColor,
                  competitor.logoTextColor
                ]"
              >
                <!-- Initials: disappear on hover -->
                <span class="z-10 transition-opacity duration-300 group-hover:opacity-0">
                  {{ competitor.initials }}
                </span>
                <!-- White plus: appears on hover -->
                <span
                  class="absolute inset-0 flex items-center justify-center text-white text-xl font-bold opacity-0 group-hover:opacity-100 transition-opacity duration-300 w-full h-full"
                >
                  +
                </span>
              </div>
              <div
                class="mt-1 text-xs text-gray-400 text-center line-clamp-2">
                {{ competitor.name }}
              </div>
            </div>

            <!-- Competitor's events grid -->
            <div
              class="relative flex-1"
              :style="{ minHeight: (Math.max(competitor.totalRows, 2) * 40) + 'px' }"
            >
              <!-- Weekend background grid -->
              <div
                class="absolute inset-0 grid gap-0"
                :style="{
                  gridTemplateColumns: 'repeat(' + daysInMonth + ', 1fr)',
                  gridTemplateRows: 'repeat(' + Math.max(competitor.totalRows, 2) + ', 1fr)'
                }"
              >
                <template v-for="rowIndex in Math.max(competitor.totalRows, 2)" :key="'row-' + rowIndex">
                  <div
                    v-for="day in daysInMonthArray"
                    :key="'bg-' + rowIndex + '-' + day"
                    :class="{ 'bg-gray-100': weekendColumns[day - 1] }"
                  ></div>
                </template>
              </div>
              <!-- Events grid for competitor -->
              <div
                class="grid gap-0 relative"
                :style="{
                  gridTemplateColumns: 'repeat(' + daysInMonth + ', 1fr)',
                  gridTemplateRows: 'repeat(' + Math.max(competitor.totalRows, 2) + ', 1fr)'
                }"
              >
                <div
                  v-for="event in competitor.events"
                  :key="event.id"
                  class="event-item relative group cursor-pointer rounded-md"
                  :class="[
                    event.itemBgColor,
                    event.itemTextColor,
                    getFeedback(event.id).flag ? 'ring-2 ' + getFlagRingClass(event) : ''
                  ]"
                  :style="{
                    gridColumn: event.startDay + ' / ' + (event.startDay + event.duration),
                    gridRow: event.row + 1,
                    padding: '2px 4px'
                  }"
                  @click="openEventModal(competitor, event)"
                  @mouseenter="hoveredEventId = event.id"
                  @mouseleave="hoveredEventId = null"
                >
                  <button
                    @click.stop="toggleEventFlag(event)"
                    class="absolute top-1 right-1 text-yellow-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  >
                    <i :class="[getFeedback(event.id).flag ? 'fas fa-flag' : 'far fa-flag']"></i>
                  </button>
                  <template v-if="event.duration > 1">
                    <span v-if="showFullText" v-auto-resize="true" class="full-text">
                      {{ event.eventTitle }}
                    </span>
                    <span v-else class="truncate" v-check-truncate="event.id">
                      {{ event.eventTitle }}
                    </span>
                  </template>
                  <template v-else>
                    <span v-if="showFullText" v-auto-resize="true" class="full-text">
                      {{ event.eventTitle }}
                    </span>
                    <span v-else class="flex items-center justify-center h-full w-full">
                      ...
                    </span>
                  </template>

                  <div
                    v-if="hoveredEventId === event.id && (event.duration === 1 || truncatedMap[event.id])"
                    class="absolute bg-black text-white text-xs rounded-md px-2 py-1 shadow-lg transition-opacity duration-300"
                    :style="{
                      bottom: 'calc(100% + 4px)',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      whiteSpace: 'pre-wrap',
                      maxWidth: '200px',
                      zIndex: 50,
                      pointerEvents: 'none'
                    }"
                  >
                    {{ event.eventTitle }}
                  </div>
                  <div
                    v-if="getFeedback(event.id).history.length"
                    class="absolute bottom-1 left-1 text-xs text-gray-500"
                    title="This event has comments"
                  >
                    <i class="fas fa-comment"></i>
                  </div>
                </div>
              </div>
            </div>
            <div
              v-if="index !== displayCompetitors.length - 1"
              class="absolute bottom-0 left-20 right-0 border-b-2 border-gray-200"
            ></div>
          </div>
        </template>
        <template v-else>
          <div class="p-4 text-center text-gray-900 mt-10 mb-10 text-xl">
            No data available for the selected month yet.
          </div>
        </template>
      </div>

      <!-- Legend -->
      <div>
        <div class="flex space-x-4 mt-5">
          <div class="flex items-center space-x-2">
            <div class="w-4 h-4 bg-red-100 border border-red-500"></div>
            <span>Sale</span>
          </div>
          <div class="flex items-center space-x-2">
            <div class="w-4 h-4 bg-blue-100 border border-blue-500"></div>
            <span>Aktion</span>
          </div>
          <div class="flex items-center space-x-2">
            <div class="w-4 h-4 bg-yellow-100 border border-yellow-500"></div>
            <span>Benefit</span>
          </div>
          <div class="flex items-center space-x-2">
            <div
              class="w-4 h-4 bg-[#B203BC] text-white rounded flex items-center justify-center"
              style="font-size: 0.75rem;"
            >
              T
            </div>
            <span>Today</span>
          </div>
        </div>
        <div class="flex items-center space-x-2">
          <span>*Calendar Elements are clickable.</span>
        </div>
      </div>

      <!-- Modal for campaign details -->
      <div
        v-if="selectedModal"
        class="fixed inset-0 bg-gray-800 bg-opacity-75 flex justify-end z-50"
        @click.self="closeEventModal"
      >
        <div class="bg-white w-1/2 h-full p-4 relative overflow-auto">
          <button class="absolute top-4 right-4 text-gray-500" @click="closeEventModal">
            <i class="fas fa-times fa-lg"></i>
          </button>
          <div class="font-semibold mb-4">
            <span
              :class="[
                'w-8 h-8 rounded-full inline-flex items-center justify-center mr-2',
                selectedModal.competitor.logoBgColor,
                selectedModal.competitor.logoTextColor
              ]"
            >
              {{ selectedModal.competitor.initials }}
            </span>
            {{ selectedModal.competitor.name }}
          </div>
          <div>
            <div class="text-3xl font-bold mb-4">
              {{ selectedModal.event.eventTitle }}
            </div>
            <span class="bg-green-100 text-green-700 px-4 py-2 font-semibold rounded-full inline-block mb-4">
              Incent: {{ selectedModal.event.incent }}
            </span>
          </div>
          <div class="text-gray-500 font-semibold mb-4">
            {{ calculateFullDuration(selectedModal.event) }} Days — {{ formatDate(selectedModal.event.originalStart) }} -
            {{ formatDate(selectedModal.event.originalEnd) }}
          </div>
              <!-- ⬇ hide / un-hide toggle lives here -->
    <button
      @click.stop="confirmToggleHidden(selectedModal.event)"
      class="ml-4 text-gray-600 hover:text-gray-800 focus:outline-none"
      :title="isEventHidden(selectedModal.event) ? 'Un-hide for tenant' : 'Hide for tenant'"
    >
      <i
        :class="[
          isEventHidden(selectedModal.event)
            ? 'fas fa-eye-slash'
            : 'far fa-eye'
        ]"
      /> Hide / Un-hide Campaign
    </button>

    <!-- inside the selectedModal block, after the Hide / Un-hide button -->
<div v-if="confirmDialog.visible && confirmDialog.event === selectedModal.event"
     class="mt-4 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded">
  <p class="text-yellow-800 mb-2">{{ confirmDialog.text }}</p>
  <div class="flex justify-end space-x-2">
    <button
      @click="onCancel"
      class="px-3 py-1 border rounded hover:bg-gray-100 text-sm"
    >
      Cancel
    </button>
    <button
      @click="onConfirm"
      class="px-3 py-1 bg-[#B203BC] text-white rounded hover:bg-[#56039A] text-sm"
    >
      Yes
    </button>
  </div>
</div>



          <!-- New Images Section: Only display if there are images -->
          <div class="mb-4" v-if="hasModalImages">
  <h3 class="text-xl font-semibold mb-2">Images</h3>
  <div class="flex overflow-x-auto space-x-4">
    <!-- Only this block remains -->
    <template v-if="selectedModal.event.dbImages && selectedModal.event.dbImages.length">
      <div
        v-for="(img, index) in selectedModal.event.dbImages"
        :key="index"
        class="relative flex-shrink-0 group"
      >
        <img
          :src="img"
          class="w-48 h-auto cursor-pointer"
          @click="openFullView(img)"
        />
        <button
          @click.stop="deleteImage(img, 'dbImages')"
          class="absolute top-0 right-0 bg-white text-gray-500 rounded-full w-5 h-5 flex items-center justify-center border border-gray-300 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          title="Delete image"
        >
          ×
        </button>
      </div>
    </template>
  </div>
</div>


          <!-- Screenshots Section -->
          <div class="mb-4">
            <h3 class="text-xl font-semibold mb-2">Upload Images</h3>
            <div class="grid grid-cols-3 gap-4">
              <!-- File input cell -->
              <div>
                <template v-if="pendingScreenshotPreview">
                  <img :src="pendingScreenshotPreview" class="w-full h-auto" />
                  <div class="mt-2 flex">
                    <button @click="confirmScreenshotUpload" class="bg-green-500 text-white px-4 py-2 rounded">
                      Confirm Upload
                    </button>
                    <button @click="cancelScreenshotUpload" class="bg-red-500 text-white px-4 py-2 rounded ml-2">
                      Cancel
                    </button>
                  </div>
                </template>
                <template v-else>
                  <label class="cursor-pointer bg-gray-200 p-2 rounded inline-block">
                    Choose File
                    <input type="file" accept="image/*" @change="handleScreenshotUpload" class="hidden" />
                  </label>
                </template>
              </div>
            </div>
          </div>

          <!-- Other modal content -->
          <div class="mb-4">
            <h3 class="text-lg font-bold">Aktions Details</h3>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-700">
              <div
                v-for="detail in selectedModal.event.aktionsDetails"
                :key="detail.label"
                :class="{ 'md:col-span-2': detail.label === 'Bedingung' }"
              >
                <span class="font-semibold">{{ detail.label }}</span>: {{ detail.value }}
              </div>
            </div>
          </div>
          <div class="mt-8 border-t pt-4">
            <h2 class="text-xl font-semibold mb-4">Bookmark & Comments


              
            </h2>
            <div class="mb-4">
              <label class="inline-flex items-center">
                <input type="checkbox" :checked="currentFeedback.flag" @change="handleFlagChange" class="form-checkbox" />
                <span class="ml-2">Bookmark this event</span>
              </label>
            </div>

            <div class="mb-4">
              <div class="chat-history rounded p-2 mb-2 max-h-40 overflow-auto">
                <div v-if="!currentFeedback.history.length" class="mb-1 text-gray-400 italic">
                  Write your first Comment...
                </div>
                <div
                  v-for="(msg, index) in currentFeedback.history"
                  :key="index"
                  class="mb-1 flex justify-between items-center"
                >
                  <div class="flex-1">
                    <div class="p-2 bg-gray-100 rounded inline-block">{{ msg.text }}</div>
                  </div>
                  <div class="flex items-center">
                    <div class="text-xs text-gray-500 ml-2">
                      {{ formatTimestamp(msg.timestamp) }}
                    </div>
                    <button @click="deleteMessage(index)" class="text-red-500 ml-2">
                      <i class="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
              </div>
              <textarea
                id="feedbackCommentary"
                v-model="newFeedbackMessage"
                rows="2"
                class="w-full border rounded p-2"
                placeholder="Type your comment..."
              ></textarea>
            </div>
            <div>
              <button
                :disabled="isSubmittingFeedback || !newFeedbackMessage.trim()"
                @click="sendFeedback"
                class="bg-[#B203BC] text-white px-4 py-2 rounded transition hover:bg-[#56039A] disabled:opacity-50"
              >
                {{ isSubmittingFeedback ? 'Sending...' : 'Send' }}
              </button>
            </div>
            <small class="block text-xs text-gray-400 mt-2">
    Messages may appear after reload the site.
  </small>
          </div>
          <div class="mt-8 border-t pt-4">
            <h2 class="text-xl font-semibold mb-4">Communication Activities</h2>
            <div v-if="getConnectedEmails(selectedModal.event).length">
              <FullContentBlock
                v-for="(email, idx) in getConnectedEmails(selectedModal.event)"
                :key="idx"
                :block="email"
              />
            </div>
            <div v-else class="text-gray-600">
              No connected activities found.
            </div>
          </div>
        </div>
      </div>

      <!-- Full View Modal -->
      <div
        v-if="fullViewImageSrc"
        class="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-50"
        @click.self="closeFullView"
      >
        <img :src="fullViewImageSrc" class="max-w-full max-h-full" />
      </div>

      <!-- Modal for Adding a New Campaign for a Competitor -->
      <div
        v-if="selectedCompetitorForNewCampaign"
        class="fixed inset-0 bg-gray-800 bg-opacity-75 flex justify-center items-center z-50"
        @click.self="closeNewCampaignModal"
      >
        <div class="bg-white w-1/2 p-4 relative">
          <button class="absolute top-4 right-4 text-gray-500" @click="closeNewCampaignModal">
            <i class="fas fa-times fa-lg"></i>
          </button>
          <h2 class="text-2xl font-bold mb-4">
            Add Campaign for {{ selectedCompetitorForNewCampaign.name }}
          </h2>
          <form @submit.prevent="handleNewCampaignSubmit">
            <div class="mb-4">
              <label for="newCampaignFromDate" class="block mb-1">From Date</label>
              <input
                id="newCampaignFromDate"
                type="date"
                v-model="newCampaignForm.fromDate"
                class="border p-2 rounded w-full"
                required
              />
            </div>
            <div class="mb-4">
              <label for="newCampaignToDate" class="block mb-1">To Date</label>
              <input
                id="newCampaignToDate"
                type="date"
                v-model="newCampaignForm.toDate"
                class="border p-2 rounded w-full"
                required
              />
            </div>
            <div class="mb-4">
              <label for="newCampaignType" class="block mb-1">Type</label>
              <select id="newCampaignType" v-model="newCampaignForm.type" class="border p-2 rounded w-full" required>
                <option value="" disabled>Select Type</option>
                <option value="sale">Sale</option>
                <option value="campaign">Aktion</option>
                <option value="special">Benefit</option>
              </select>
            </div>
            <div class="mb-4">
              <label for="newCampaignIncent" class="block mb-1">Campaign Title</label>
              <input
                id="newCampaignIncent"
                type="text"
                v-model="newCampaignForm.incent"
                class="border p-2 rounded w-full"
              />
            </div>
            <div class="flex justify-end">
              <button
                type="button"
                @click="closeNewCampaignModal"
                class="mr-4 px-4 py-2 border rounded hover:bg-gray-100"
              >
                Cancel
              </button>
              <button type="submit" class="bg-[#B203BC] text-white px-4 py-2 rounded hover:bg-[#56039A]">
                Create Campaign
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
  <div v-else class="p-4">
    Loading...
  </div>
</template>

<script>
import { ref, computed, onMounted, reactive, watch } from 'vue';
import html2canvas from 'html2canvas';
import { useMainStore } from '@/stores/useMainStore';
import FullContentBlock from '@/components/FullContentBlock.vue';

const checkTruncate = {
  mounted(el, binding) {
    setTimeout(() => {
      binding.instance.truncatedMap[binding.value] = el.scrollWidth > el.clientWidth;
    }, 0);
  },
  updated(el, binding) {
    setTimeout(() => {
      binding.instance.truncatedMap[binding.value] = el.scrollWidth > el.clientWidth;
    }, 0);
  }
};

const autoResize = {
  mounted(el, binding) {
    if (binding.value) {
      optimizeTextSize(el);
    }
  },
  updated(el, binding) {
    if (binding.value) {
      optimizeTextSize(el);
    } else if (el.__originalFontSize) {
      el.style.fontSize = el.__originalFontSize + 'px';
      el.style.whiteSpace = '';
      el.style.overflow = '';
      el.style.wordBreak = '';
    }
  }
};

function optimizeTextSize(el) {
  // Reset inline font-size to read natural computed size.
  el.style.fontSize = '';
  const computedStyle = window.getComputedStyle(el);
  let maxFontSize = parseFloat(computedStyle.fontSize);

  if (!el.__originalFontSize) {
    el.__originalFontSize = maxFontSize;
  }

  // Binary search to find largest size that fits container.
  let min = 6;
  let max = el.__originalFontSize;
  let optimal = min;
  el.style.whiteSpace = 'normal';
  el.style.overflow = 'visible';
  el.style.wordBreak = 'break-word';

  while (max - min > 0.5) {
    const mid = (min + max) / 2;
    el.style.fontSize = mid + 'px';
    if (el.scrollWidth <= el.clientWidth && el.scrollHeight <= el.clientHeight) {
      optimal = mid;
      min = mid;
    } else {
      max = mid;
    }
  }
  el.style.fontSize = optimal + 'px';

  // Determine line count at this size.
  let lineHeight = computedStyle.lineHeight;
  if (lineHeight === 'normal') {
    lineHeight = 1.2 * optimal;
  } else {
    lineHeight = parseFloat(lineHeight);
  }
  const lineCount = Math.ceil(el.scrollHeight / lineHeight);
  const maxAllowedLines = 2; // Change as desired.

  // If more than allowed lines, do a second search to further reduce font size.
  if (lineCount > maxAllowedLines) {
    let min2 = 6;
    let max2 = optimal;
    let optimal2 = min2;
    while (max2 - min2 > 0.5) {
      const mid2 = (min2 + max2) / 2;
      el.style.fontSize = mid2 + 'px';
      const styleAtMid = window.getComputedStyle(el);
      let lh = styleAtMid.lineHeight;
      if (lh === 'normal') {
        lh = 1.2 * mid2;
      } else {
        lh = parseFloat(lh);
      }
      const currentLineCount = Math.ceil(el.scrollHeight / lh);
      if (currentLineCount <= maxAllowedLines) {
        optimal2 = mid2;
        min2 = mid2;
      } else {
        max2 = mid2;
      }
    }
    el.style.fontSize = optimal2 + 'px';
  }
}

export default {
  name: 'CalendarView',
  directives: {
    checkTruncate,
    autoResize
  },
  components: { FullContentBlock },
  setup() {

    // at top of setup()
    const confirmDialog = reactive({
  visible: false,
  event:   null,
  action:  null,     // just null, no "as ..." type assertion
  text:    '',
});


// when the user *really* clicks “Yes”
async function onConfirm() {
  if (!confirmDialog.action) return;
  try {
    await confirmDialog.action();
  } finally {
    confirmDialog.visible = false;
    confirmDialog.event   = null;
    confirmDialog.action  = null;
  }
}

// when the user clicks “No”
function onCancel() {
  confirmDialog.visible = false;
  confirmDialog.event   = null;
  confirmDialog.action  = null;
}


    const store = useMainStore();
    const currentDate = new Date();
    const selectedMonth = ref(currentDate.getMonth());
    const selectedYear = ref(currentDate.getFullYear());
    const months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December'
    ];
    const years = [2024, 2025];
    const fullViewImageSrc = ref(null);
    const dataReady = ref(false);
    const selectedModal = ref(null);
    const truncatedMap = reactive({});
    const hoveredEventId = ref(null);
    const feedbackData = reactive({});
    const { isEventHidden } = store;

    

    // New: ref for screenshot area
    const screenshotArea = ref(null);
    // New: reactive array to store screenshot breadcrumb logs
    const screenshotBreadcrumbs = ref([]);

    // New: captureScreenshot method (updated)
    const captureScreenshot = async () => {
  if (!screenshotArea.value) return;
  try {
    // 1) Wait for fonts to load to help avoid “falling text”
    await document.fonts.ready;

    // 2) Capture screenshot using html2canvas
    const originalCanvas = await html2canvas(screenshotArea.value, {
      scale: 2,
      useCORS: true,
      // This undocumented property sometimes helps align text more accurately
      letterRendering: 2,
      onclone: (clonedDoc) => {
        // Remove transitions
        clonedDoc.querySelectorAll('*').forEach(el => {
          el.style.transition = 'none';
          el.style.webkitTransition = 'none';
        });

        // Inject a style block to normalize line-heights and smoothing
        const style = clonedDoc.createElement('style');
        style.textContent = `
          * {
            line-height: normal !important;
            -webkit-font-smoothing: antialiased !important;
            -moz-osx-font-smoothing: grayscale !important;
          }
        `;
        clonedDoc.head.appendChild(style);
      }
    });

    // 3) Post-process the rendered image: shift it upward slightly
    const verticalOffset = -3;
    const adjustedCanvas = document.createElement('canvas');
    adjustedCanvas.width = originalCanvas.width;
    adjustedCanvas.height = originalCanvas.height;

    const ctx = adjustedCanvas.getContext('2d');
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, adjustedCanvas.width, adjustedCanvas.height);
    ctx.drawImage(originalCanvas, 0, verticalOffset);

    // 4) Convert final canvas to Blob and copy to system clipboard as image/png
    const blob = await new Promise((resolve) => {
      adjustedCanvas.toBlob(resolve, 'image/png');
    });
    if (!blob) {
      throw new Error('Failed to create image blob');
    }
    await navigator.clipboard.write([
      new ClipboardItem({ 'image/png': blob })
    ]);

    // 5) Create a breadcrumb log entry and then fade it out after 3 seconds
    screenshotBreadcrumbs.value.push(`Screenshot added to clipboard! ✅`);

    // Remove the breadcrumb after 3 seconds to allow the fade transition
    setTimeout(() => {
      // Remove the first log message (or adjust the logic if you expect multiple messages)
      screenshotBreadcrumbs.value.shift();
    }, 3000);
  } catch (error) {
    console.error("Error capturing screenshot:", error);
  }
};


    const showFullText = ref(false);

    const getFeedback = (eventId) => {
      if (!feedbackData[eventId]) {
        feedbackData[eventId] = reactive({ flag: false, history: [] });
      }
      return feedbackData[eventId];
    };

    // Helper to build full image URL from filename.
    const getImagePath = (imageName) =>
      `https://rwnbpxpxauytxfuuffsg.supabase.co/storage/v1/object/public/screenshots/${imageName}`;

    // Fallback thumbnail is now only used elsewhere; here we hide the section if no images.
    const getFallbackThumbnail = (event) => {
      const type = event.type ? event.type.toLowerCase() : '';
      if (type === 'sale') return require('@/assets/images/sale.png');
      if (type === 'campaign' || type === 'aktion') return require('@/assets/images/campaign.png');
      if (type === 'special') return require('@/assets/images/special.png');
      return require('@/assets/images/default.jpg');
    };

    // Computed property to determine if the modal has images to display.
    const hasModalImages = computed(() => {
  return (
    selectedModal.value?.event?.dbImages?.length > 0
  );
});


    const toggleEventFlag = async (event) => {
      const fb = getFeedback(event.id);
      fb.flag = !fb.flag;
      try {
        await store.updateEventFlag(event.aktionKey || event.id, fb.flag);
        console.log('Event flag updated:', fb.flag);
      } catch (error) {
        console.error('Error updating event flag:', error);
      }
    };

    const handleFlagChange = async () => {
      if (!selectedModal.value) return;
      await toggleEventFlag(selectedModal.value.event);
    };

    const getFlagRingClass = (event) => {
      const mapping = {
        'bg-red-100': 'ring-red-500',
        'bg-blue-100': 'ring-blue-500',
        'bg-yellow-100': 'ring-yellow-500'
      };
      return mapping[event.itemBgColor] || 'ring-blue-500';
    };

    const currentFeedback = computed(() => {
      if (!selectedModal.value) return { flag: false, history: [] };
      return getFeedback(selectedModal.value.event.id);
    });

    const newFeedbackMessage = ref('');
    const isSubmittingFeedback = ref(false);

    const formatTimestamp = (timestamp) => {
      return new Date(timestamp).toLocaleTimeString();
    };

    const deleteMessage = async (index) => {
  if (!selectedModal.value) return;

  // Use the feedback history from the campaign (keyed by event.id)
  const campaignId = selectedModal.value.event.id;
  const history = getFeedback(campaignId).history;
  
  // Check that the message exists at the given index
  if (!history || index < 0 || index >= history.length) {
    console.error('No message found at index', index, 'for campaign', campaignId);
    return;
  }
  
  const msg = history[index];
  
  // Check if the message object has an id
  if (!msg || msg.id === undefined) {
    console.error('Message id is undefined for index', index, 'in campaign', campaignId);
    return;
  }
  
  try {
    // Now pass only msg.id to the deleteMessage method.
    await store.deleteMessage(msg.id);
    history.splice(index, 1);
  } catch (error) {
    console.error('Error deleting message:', error);
  }
};




    const activeEventTypeFilters = ref([]);
    const eventTypeFilters = computed(() => {
      return [
        {
          name: 'Sale',
          displayName: 'Sale',
          badgeClass: 'outline outline-red-500 bg-red-200',
          textClass: 'text-black text-xs'
        },
        {
          name: 'Campaign',
          displayName: 'Aktion',
          badgeClass: 'outline outline-blue-500 bg-blue-200',
          textClass: 'text-black text-xs'
        },
        {
          name: 'Special',
          displayName: 'Benefit',
          badgeClass: 'outline outline-yellow-500 bg-yellow-200',
          textClass: 'text-black text-xs'
        }
      ];
    });
    function toggleEventTypeFilter(filterName) {
      if (filterName === 'all') {
        activeEventTypeFilters.value = [];
      } else {
        if (activeEventTypeFilters.value.includes(filterName)) {
          activeEventTypeFilters.value = activeEventTypeFilters.value.filter(
            (f) => f !== filterName
          );
        } else {
          activeEventTypeFilters.value.push(filterName);
        }
      }
    }

    const daysInMonth = computed(() => new Date(selectedYear.value, selectedMonth.value + 1, 0).getDate());
    const daysInMonthArray = computed(() => Array.from({ length: daysInMonth.value }, (_, i) => i + 1));
    const weekendColumns = computed(() => {
      const arr = [];
      for (let day = 1; day <= daysInMonth.value; day += 1) {
        const d = new Date(selectedYear.value, selectedMonth.value, day);
        d.setHours(0, 0, 0, 0);
        arr.push(d.getDay() === 0 || d.getDay() === 6);
      }
      return arr;
    });

    const todayDay = computed(() => {
      const now = new Date();
      return selectedMonth.value === now.getMonth() && selectedYear.value === now.getFullYear()
        ? now.getDate()
        : null;
    });

    // Only "my own" campaigns (no competitorId) for the planner row.
    const plannedEvents = computed(() => {
      const monthStart = new Date(selectedYear.value, selectedMonth.value, 1);
      monthStart.setHours(0, 0, 0, 0);
      const monthEnd = new Date(selectedYear.value, selectedMonth.value, daysInMonth.value);
      monthEnd.setHours(23, 59, 59, 999);
      return store.plannedCampaigns
        .filter((campaign) => !campaign.competitorId)
        .map((campaign) => {
          if (!campaign.fromDate || !campaign.toDate) return null;
          const start = new Date(campaign.fromDate);
          start.setHours(0, 0, 0, 0);
          const end = new Date(campaign.toDate);
          end.setHours(0, 0, 0, 0);
          if (end < monthStart || start > monthEnd) return null;
          const originalStart = new Date(start.getTime());
          const originalEnd = new Date(end.getTime());
          const actualStart = start < monthStart ? monthStart : start;
          const actualEnd = end > monthEnd ? monthEnd : end;
          const startDay = actualStart.getDate();
          const endDay = actualEnd.getDate();
          const duration = endDay - startDay + 1;
          return {
            id: campaign.id,
            type: campaign.type,
            incent: campaign.incent,
            startDay,
            duration,
            originalStart,
            originalEnd,
            images: campaign.screenshots
              ? campaign.screenshots
                  .split(',')
                  .map((filename) => getImagePath(filename.trim()))
                  .filter(Boolean)
              : [],
            dbImages: campaign.screenshots
              ? campaign.screenshots
                  .split(',')
                  .map((filename) => getImagePath(filename.trim()))
                  .filter(Boolean)
              : [],
            screenshots: campaign.screenshots || ""
          };
        })
                .filter((e) => e !== null)
                .filter((e) => !isEventHidden(e));
    });

    // Build competitors from dataList2 and merge planned competitor campaigns (those with competitorId)
    const competitors = computed(() => {
      const competitorMap = {};
      if (store.dataList2 && store.dataList2.length) {
        store.dataList2.forEach((item) => {
          const abbreviation =
            item.abbreviation ||
            (item.name ? item.name.match(/\b\w/g).join('') : 'UC');
          if (!competitorMap[abbreviation]) {
            competitorMap[abbreviation] = {
              abbreviation,
              id: abbreviation,
              name: item.name || 'Unknown Company',
              initials: abbreviation,
              logoBgColor: 'bg-gray-400',
              logoTextColor: 'text-gray-900',
              events: []
            };
          }
          competitorMap[abbreviation].events.push({
            id: item.kanalid || item.aktion_key,
            kanalid: item.kanalid,
            aktionKey: item.aktion_key,
            startDate: item.von,
            endDate: item.bis,
            eventTitle: item.calender_name || item.aktion_compose_name || item.betreff,
            itemBgColor:
              item.aktions_typ === 'Sale'
                ? 'bg-red-100'
                : item.aktions_typ === 'Aktion'
                  ? 'bg-blue-100'
                  : 'bg-yellow-100',
            itemTextColor: 'text-black text-center',
            images:
              item.aktions_typ && ['aktion', 'campaign', 'sale', 'special'].includes(item.aktions_typ.toLowerCase())
                ? [
                    {
                      src: require(`@/assets/images/${
                        item.aktions_typ.toLowerCase() === 'sale'
                          ? 'sale.png'
                          : item.aktions_typ.toLowerCase() === 'aktion' || item.aktions_typ.toLowerCase() === 'campaign'
                          ? 'campaign.png'
                          : 'special.png'
                      }`),
                      error: false,
                      fallback: require('@/assets/images/default.jpg')
                    }
                  ]
                : [],
            // For kanal_composed events, dbImages come from screenshotlink2
            dbImages: item.screenshotlink2
              ? item.screenshotlink2
                  .split(',')
                  .map((filename) => getImagePath(filename.trim()))
                  .filter(Boolean)
              : [],
            incent: item.incentivierung,
            aktionsDetails: [
              { label: 'Incent. Art', value: item.incentivierungsart },
              { label: 'Bedingung', value: (item.bedingung || '').replace(/^\{|\}$/g, '') },
              { label: 'Sichtbarkeit', value: item.sichtbarkeit },
              { label: 'Sortiment', value: item.sortiment },
              { label: 'Gutscheincode', value: item.gutscheincode || 'Nein' }
            ],
            actionType:
              item.aktions_typ === 'Sale'
                ? 'Sale'
                : item.aktions_typ === 'Aktion'
                  ? 'Campaign'
                  : 'Special'
          });
        });
      }
      // Merge planned competitor campaigns (those with competitorId) into competitorMap
      if (store.plannedCampaigns && store.plannedCampaigns.length) {
        store.plannedCampaigns
          .filter((campaign) => campaign.competitorId)
          .forEach((campaign) => {
            const competitor = Object.values(competitorMap).find(
              (c) => c.id === campaign.competitorId
            );
            if (competitor) {
              competitor.events.push({
                id: campaign.id,
                aktionKey: campaign.id,
                startDate: campaign.fromDate,
                endDate: campaign.toDate,
                eventTitle: campaign.incent,
                itemBgColor: campaign.type === 'sale'
                  ? 'bg-red-100'
                  : campaign.type === 'campaign'
                    ? 'bg-blue-100'
                    : 'bg-yellow-100',
                itemTextColor: 'text-black text-center',
                images: [],
                dbImages: campaign.screenshots
                  ? campaign.screenshots
                      .split(',')
                      .map((filename) => getImagePath(filename.trim()))
                      .filter(Boolean)
                  : [],
                screenshots: campaign.screenshots || "",
                incent: campaign.incent,
                actionType: campaign.type === 'sale'
                  ? 'Sale'
                  : campaign.type === 'campaign'
                    ? 'Campaign'
                    : 'Special'
              });
            }
          });
      }
      let competitorsArr = Object.values(competitorMap);
      if (store.sidebarItems && store.sidebarItems.length) {
        const sidebarColors = {};
        store.sidebarItems.forEach((item) => {
          const abbr =
            item.abbreviation ||
            (item.abbrevation ? item.abbrevation.toUpperCase() : 'N/A');
          sidebarColors[abbr] = {
            backgroundColor: item.backgroundColor,
            textColor: item.textColor
          };
        });
        Object.values(competitorMap).forEach((comp) => {
          if (sidebarColors[comp.abbreviation]) {
            comp.logoBgColor = sidebarColors[comp.abbreviation].backgroundColor || 'bg-gray-400';
            comp.logoTextColor = sidebarColors[comp.abbreviation].textColor || 'text-gray-900';
          }
        });
      }
      if (store.sidebarItems && store.sidebarItems.length) {
        const orderMap = {};
        store.sidebarItems.forEach((item, index) => {
          const abbr =
            item.abbreviation ||
            (item.abbrevation ? item.abbrevation.toUpperCase() : 'N/A');
          orderMap[abbr] = index;
        });
        competitorsArr.sort((a, b) => {
          // Force "Breuninger" to the top
          if (a.name === 'Breuninger') return -1;
          if (b.name === 'Breuninger') return 1;
          const orderA = orderMap[a.abbreviation] ?? 999;
          const orderB = orderMap[b.abbreviation] ?? 999;
          return orderA - orderB;
        });
      }
      return competitorsArr;
    });

    const filteredCompetitors = computed(() => {
      const monthStart = new Date(selectedYear.value, selectedMonth.value, 1);
      monthStart.setHours(0, 0, 0, 0);
      const monthEnd = new Date(selectedYear.value, selectedMonth.value, daysInMonth.value);
      monthEnd.setHours(23, 59, 59, 999);
      return competitors.value.map((comp) => {
        const events = comp.events
          .map((evt) => {
            if (!evt.startDate || !evt.endDate) return null;
            const start = new Date(evt.startDate);
            start.setHours(0, 0, 0, 0);
            const end = new Date(evt.endDate);
            end.setHours(0, 0, 0, 0);
            if (end < monthStart || start > monthEnd) return null;
            const originalStart = new Date(start.getTime());
            const originalEnd = new Date(end.getTime());
            const actualStart = start < monthStart ? monthStart : start;
            const actualEnd = end > monthEnd ? monthEnd : end;
            const startDay = actualStart.getDate();
            const endDay = actualEnd.getDate();
            const duration = endDay - startDay + 1;
            return {
              ...evt,
              originalStart,
              originalEnd,
              eventStart: actualStart,
              eventEnd: actualEnd,
              startDay,
              endDay,
              duration,
              multiMonth:
                originalStart.getMonth() !== originalEnd.getMonth() ||
                originalStart.getFullYear() !== originalEnd.getFullYear()
            };
          })
          .filter((evt) => evt !== null)
          .filter((evt) => !isEventHidden(evt));

        if (events.length > 0) {
          events.sort((a, b) => a.startDay - b.startDay);
        }

        const eventRows = [];
        events.forEach((e) => {
          let placed = false;
          for (let i = 0; i < eventRows.length; i++) {
            const rowEvents = eventRows[i];
            const overlap = rowEvents.some(
              (r) => !(e.endDay < r.startDay || e.startDay > r.endDay)
            );
            if (!overlap) {
              e.row = i;
              rowEvents.push(e);
              placed = true;
              break;
            }
          }
          if (!placed) {
            e.row = eventRows.length;
            eventRows.push([e]);
          }
        });
        return { ...comp, events, totalRows: Math.max(eventRows.length, 2) };
      });
    });

    const activeCompetitorFilters = ref(['all']);
    const competitorFilters = computed(() => {
      return [
        {
          name: 'all',
          label: 'All',
          displayName: 'All',
          badgeClass: 'bg-black',
          textClass: 'text-white text-xs'
        },
        {
          name: 'planned',
          label: 'P',
          displayName: 'Planned',
          badgeClass: 'bg-gray-300',
          textClass: 'text-gray-900 text-xs'
        },
        ...competitors.value.map((comp) => ({
          name: comp.id,
          label: comp.initials,
          displayName: comp.name,
          badgeClass: comp.logoBgColor,
          textClass: comp.logoTextColor
        }))
      ];
    });
    function toggleCompetitorFilter(filterName) {
      if (filterName === 'all') {
        activeCompetitorFilters.value = ['all'];
        activeEventTypeFilters.value = [];
      } else {
        if (activeCompetitorFilters.value.includes(filterName)) {
          activeCompetitorFilters.value = activeCompetitorFilters.value.filter(
            (f) => f !== filterName
          );
        } else {
          activeCompetitorFilters.value = activeCompetitorFilters.value.filter(
            (f) => f !== 'all'
          );
          activeCompetitorFilters.value.push(filterName);
        }
        if (activeCompetitorFilters.value.length === 0) {
          activeCompetitorFilters.value = ['all'];
        }
      }
    }

    const showPlanned = computed(() => {
      return (
        activeCompetitorFilters.value.includes('all') ||
        activeCompetitorFilters.value.includes('planned')
      );
    });

    const displayCompetitors = computed(() => {
      let comps = filteredCompetitors.value;
      if (!activeCompetitorFilters.value.includes('all')) {
        comps = comps.filter((comp) =>
          activeCompetitorFilters.value.includes(comp.id)
        );
      }
      if (activeEventTypeFilters.value.length > 0) {
        comps = comps
          .map((comp) => {
            const filteredEvents = comp.events.filter((e) =>
              activeEventTypeFilters.value.includes(e.actionType)
            );
            filteredEvents.sort((a, b) => a.startDay - b.startDay);
            const eventRows = [];
            filteredEvents.forEach((e) => {
              let placed = false;
              for (let i = 0; i < eventRows.length; i++) {
                const rowEvents = eventRows[i];
                const overlap = rowEvents.some(
                  (r) => !(e.endDay < r.startDay || e.startDay > r.endDay)
                );
                if (!overlap) {
                  e.row = i;
                  rowEvents.push(e);
                  placed = true;
                  break;
                }
              }
              if (!placed) {
                e.row = eventRows.length;
                eventRows.push([e]);
              }
            });
            return { ...comp, events: filteredEvents, totalRows: eventRows.length };
          })
          .filter((comp) => comp.events.length > 0);
      }
      return comps;
    });

    const preloadEventDataForVisibleEvents = async () => {
      const comps = displayCompetitors.value;
      const promises = [];
      comps.forEach((comp) => {
        comp.events.forEach((evt) => {
          const eventId = evt.aktionKey || evt.id;
          const fb = getFeedback(evt.id);
          promises.push(
            store
              .fetchFlag(eventId)
              .then((flagData) => {
                fb.flag = typeof flagData.flagged === 'boolean' ? flagData.flagged : false;
              })
              .catch((error) => {
                console.error(`Error fetching flag for event ${eventId}:`, error);
              })
          );
          promises.push(
            store
              .fetchMessages(eventId)
              .then((messages) => {
                fb.history = messages.map((m) => ({
                  text: m.message,
                  timestamp: m.created_at
                }));
              })
              .catch((error) => {
                console.error(`Error fetching messages for event ${eventId}:`, error);
              })
          );
        });
      });
      await Promise.all(promises);
    };

    watch(
      displayCompetitors,
      () => {
        preloadEventDataForVisibleEvents();
      },
      { immediate: true }
    );

    onMounted(async () => {
      await store.fetchHiddenSets();

      if (!store.sidebarItems.length) {
        await store.fetchSidebarItems();
      }
      if (!store.dataList2.length) {
        await store.fetchWithAbbreviations();
      }
      await store.fetchPlannedCampaigns();
      await store.fetchData();
      while (!store.allDataLoaded) {
        await store.loadMoreData();
      }
      dataReady.value = true;
    });

// inside your setup() in CalendarView.vue

const openEventModal = async (competitor, event) => {
  // 1) load bookmark‐flag & comments
  const eventId = event.aktionKey || event.id;
  const fb = getFeedback(event.id);

  try {
    const flagData = await store.fetchFlag(eventId);
    fb.flag = !!flagData.flagged;

    const messages = await store.fetchMessages(eventId);
    fb.history = messages.map(m => ({
      id: m.id,
      text: m.message,
      timestamp: m.created_at
    }));
  } catch (err) {
    console.error('Error loading flag/messages:', err);
  }

  // 2) load screenshots for this tenant only
  if (event.kanalid != null) {
    const files = await store.fetchKanalScreenshots(event.kanalid);
    event.dbImages = files.map(name => getImagePath(name));
  } else {
    const files = await store.fetchPlannedScreenshots(event.id);
    event.dbImages = files.map(name => getImagePath(name));
  }

  // 3) finally open the modal
  selectedModal.value = { competitor, event };
};


    const closeEventModal = () => {
      selectedModal.value = null;
      newFeedbackMessage.value = '';
    };

    const prevMonth = () => {
      if (selectedMonth.value === 0) {
        selectedMonth.value = 11;
        selectedYear.value -= 1;
      } else {
        selectedMonth.value -= 1;
      }
    };

    const nextMonth = () => {
      if (selectedMonth.value === 11) {
        selectedMonth.value = 0;
        selectedYear.value += 1;
      } else {
        selectedMonth.value += 1;
      }
    };

    const goToToday = () => {
      const now = new Date();
      selectedMonth.value = now.getMonth();
      selectedYear.value = now.getFullYear();
    };

    const openFullView = (src) => {
      fullViewImageSrc.value = src;
    };

    const closeFullView = () => {
      fullViewImageSrc.value = null;
    };

    const getConnectedEmails = (event) =>
      store.contentBlocks.filter((block) => block.aktionKey === event.aktionKey);
    const formatDate = (date) =>
      date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const calculateFullDuration = (event) => {
      const diffTime = event.originalEnd.getTime() - event.originalStart.getTime();
      const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24)) + 1;
      return diffDays;
    };

    const sendFeedback = async () => {
      if (!selectedModal.value || !newFeedbackMessage.value.trim()) return;
      const eventId = selectedModal.value.event.aktionKey || selectedModal.value.event.id;
      const messageObj = {
        text: newFeedbackMessage.value,
        timestamp: new Date().toISOString()
      };
      getFeedback(eventId).history.push(messageObj);
      newFeedbackMessage.value = '';
      isSubmittingFeedback.value = true;
      try {
        await store.submitEventFeedback(eventId, getFeedback(eventId).flag, messageObj);
        console.log('Feedback submitted');
      } catch (error) {
        console.error('Error sending feedback:', error);
      } finally {
        isSubmittingFeedback.value = false;
      }
    };

    // New reactive state and methods for adding a campaign via the competitor bubble
    const selectedCompetitorForNewCampaign = ref(null);
    const newCampaignForm = reactive({
      fromDate: '',
      toDate: '',
      type: '',
      incent: ''
    });
    const openAddCampaignModal = (competitor) => {
      selectedCompetitorForNewCampaign.value = competitor;
      newCampaignForm.fromDate = '';
      newCampaignForm.toDate = '';
      newCampaignForm.type = '';
      newCampaignForm.incent = '';
    };
    const closeNewCampaignModal = () => {
      selectedCompetitorForNewCampaign.value = null;
    };

    const handleNewCampaignSubmit = async () => {
      if (!selectedCompetitorForNewCampaign.value) return;
      try {
        await store.createPlannedCampaign({
          fromDate: newCampaignForm.fromDate,
          toDate: newCampaignForm.toDate,
          type: newCampaignForm.type,
          incent: newCampaignForm.incent,
          competitorId: selectedCompetitorForNewCampaign.value.id
        });
        await store.fetchPlannedCampaigns();
        closeNewCampaignModal();
      } catch (error) {
        console.error('Error creating campaign:', error);
      }
    };

    const pendingScreenshotFile = ref(null);
    const pendingScreenshotPreview = ref(null);

    const handleScreenshotUpload = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      pendingScreenshotFile.value = file;
      // Create a local preview URL
      pendingScreenshotPreview.value = URL.createObjectURL(file);
    };

    const confirmScreenshotUpload = async () => {
  if (!pendingScreenshotFile.value) return;

  try {
    /** 
     * uploadScreenshot()
     * – uploads the file to the bucket,
     * – inserts the mapping row via the Edge function,
     * – and already pushes the public URL into event.dbImages.
     */
    await store.uploadScreenshot(
      selectedModal.value.event,
      pendingScreenshotFile.value
    );
  } catch (error) {
    console.error('Error during upload confirmation:', error);
  } finally {
    pendingScreenshotFile.value   = null;
    pendingScreenshotPreview.value = null;
  }
};



    const cancelScreenshotUpload = () => {
      pendingScreenshotFile.value = null;
      pendingScreenshotPreview.value = null;
    };

    const deleteImage = async (imgUrl, type) => {
      // Extract the filename from the URL
      const filename = imgUrl.split('/').pop();
      if (!selectedModal.value) return;
      try {
        await store.deleteScreenshot(selectedModal.value.event, filename);
        // Update local state: remove the deleted filename from the appropriate field.
        if (type === 'screenshots') {
          const filenames = selectedModal.value.event.screenshots
            .split(',')
            .map(s => s.trim())
            .filter(f => f && f !== filename);
          selectedModal.value.event.screenshots = filenames.join(',');
        } else if (type === 'dbImages') {
          selectedModal.value.event.dbImages = selectedModal.value.event.dbImages.filter(url => {
            return url.split('/').pop() !== filename;
          });
        }
      } catch (error) {
        console.error("Error deleting image:", error);
      }
    };

   const confirmToggleHidden = (event) => {
   const wantHide = !store.isEventHidden(event);
   confirmDialog.visible = true;
   confirmDialog.event   = event;
   confirmDialog.text    = wantHide
     ? 'Hide this campaign from your calendar?'
     : 'Show this campaign again?';
   confirmDialog.action  = async () => {
     try {
       await store.toggleEventHidden(event);
     } catch (e) {
       // you could also drive an error-toast here
       console.error(e);
     }
   };
 };


    return {
      selectedMonth,
      selectedYear,
      months,
      years,
      dataReady,
      daysInMonth,
      daysInMonthArray,
      weekendColumns,
      displayCompetitors,
      showFullText,
      openEventModal,
      closeEventModal,
      openFullView,
      closeFullView,
      fullViewImageSrc,
      handleScreenshotUpload,
      confirmScreenshotUpload,
      cancelScreenshotUpload,
      pendingScreenshotPreview,
      getConnectedEmails,
      formatDate,
      prevMonth,
      nextMonth,
      goToToday,
      selectedModal,
      calculateFullDuration,
      todayDay,
      competitorFilters,
      activeCompetitorFilters,
      isEventHidden,
      confirmToggleHidden,
      confirmDialog,
      onConfirm,
      onCancel,
      toggleCompetitorFilter,
      eventTypeFilters,
      activeEventTypeFilters,
      toggleEventTypeFilter,
      getFeedback,
      toggleEventFlag,
      getFlagRingClass,
      currentFeedback,
      newFeedbackMessage,
      isSubmittingFeedback,
      sendFeedback,
      formatTimestamp,
      deleteMessage,
      handleFlagChange,
      hoveredEventId,
      deleteImage,
      truncatedMap,
      plannedEvents,
      showPlanned,
      selectedCompetitorForNewCampaign,
      newCampaignForm,
      openAddCampaignModal,
      closeNewCampaignModal,
      handleNewCampaignSubmit,
      getImagePath,
      getFallbackThumbnail,
      hasModalImages,
      captureScreenshot,
      screenshotArea,
      screenshotBreadcrumbs
    };
  }
};
</script>

<style scoped>
.timeline-header {
  display: grid;
  gap: 0;
}

.event-item {
  margin: 0.25rem;
  box-sizing: border-box;
}

.truncate {
  display: block;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.full-text {
  display: block;
  width: 100%;
  /* No forced font-size here! */
  white-space: normal;
  word-break: break-word;
  overflow-wrap: break-word;
}

.image-preview {
  cursor: pointer;
}

.filter-btn {
  transition: all 0.3s ease;
}

.filter-btn.active {
  background-color: #B203BC;
  color: white;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.fluted-event-item {
  background-image: repeating-linear-gradient(
    45deg,
    transparent,
    transparent 2px,
    rgba(0, 0, 0, 0.05) 2px,
    rgba(0, 0, 0, 0.05) 4px
  );
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 1s ease;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>